import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCube,
    FaExchangeAlt,
    FaFingerprint,
    FaGlobeAmericas,
    FaLeaf,
    FaLock,
    FaPaw,
    FaShieldAlt,
    FaTerminal,
    FaUsers
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChevronRight,
    TiFlash
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const NFTMarketplace = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const impactStats = [
    { value: "$15.8M+", label: "Total Volume", icon: <FaExchangeAlt className="text-cyan-400" />, sub: "Institutional Liquidity" },
    { value: "48", label: "Species Protected", icon: <FaPaw className="text-purple-400" />, sub: "DAO-Led Preservation" },
    { value: "28,400+", label: "Acres Secured", icon: <FaLeaf className="text-emerald-400" />, sub: "Environmental Reserve" },
    { value: "1,250+", label: "Verified Creators", icon: <FaUsers className="text-orange-400" />, sub: "Artistic Visionaries" },
  ];

  const collections = [
    {
      title: "The Apex Series",
      desc: "1:1 ultra-rare digital masterpieces of the planet's most endangered giants.",
      image: "https://images.unsplash.com/photo-1564419320461-6870880221ad?q=80&w=2000&auto=format&fit=crop",
      tag: "Legendary"
    },
    {
      title: "Provenance Fine Art",
      desc: "Fractionalised ownership of world-renowned physical art pieces backed by Zero-Art.",
      image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?q=80&w=2000&auto=format&fit=crop",
      tag: "Institutional"
    },
    {
      title: "The Impact Pass",
      desc: "Utility-driven membership NFTs conferring DAO voting power and early whitelist access.",
      image: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?q=80&w=2000&auto=format&fit=crop",
      tag: "Utility"
    },
    {
      title: "Community Drops",
      desc: "A curated space for emerging sustainable digital artists and impact-driven creators.",
      image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2000&auto=format&fit=crop",
      tag: "Curated"
    }
  ];

  const techFeatures = [
    {
      title: "Immutable Provenance",
      description: "Asset metadata is secured permanently on Arweave and IPFS, ensuring your digital heritage never disappears.",
      icon: <FaFingerprint />,
      list: ["Arweave Permanent Storage", "IPFS Content Addressing", "On-Chain Royalties"]
    },
    {
      title: "Multi-Chain Hub",
      description: "Zero-Art bridges the world's most liquid networks, enabling cross-chain trading and fractionalisation.",
      icon: <FaCube />,
      list: ["ERC-721/1155 Support", "Cross-Chain Bridges", "ZK-Proof Verification"]
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 shadow-[0_0_80px_rgba(34,211,238,0.15)] bg-black">
          <video
            src="/videos/nftmarketplace-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="nftHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Digital Conservation Engine"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Zero-Art: Bridging Fine Art with Planetary Impact
            </motion.p>
            <GlassButton
              onClick={() => window.open('https://zeroartnft.xyz', '_blank')}
              variant="cyan"
              className="mt-12 px-10 py-4"
              containerClass="z-10"
            >
              <div className="flex items-center gap-3">
                EXPLORE ZERO-ART <TiArrowRight className="text-xl" />
              </div>
            </GlassButton>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Marketplace Impact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48" ref={(el) => (sectionRefs.current[0] = el)}>
           {impactStats.map((stat, index) => (
             <motion.div
               key={index}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: index * 0.1 }}
               className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:border-cyan-500/40 transition-all group relative overflow-hidden"
             >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-4xl mb-6 group-hover:scale-110 transition-transform origin-left">{stat.icon}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">{stat.label}</div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-[10px] text-emerald-400/80 font-medium flex items-center gap-2 italic">
                   <FaGlobeAmericas className="text-sm" /> {stat.sub}
                </div>
             </motion.div>
           ))}
        </div>

        {/* Collection Bento Grid */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[1] = el)}>
           <div className="flex flex-col md:flex-row items-end justify-between mb-16 gap-8">
              <div className="max-w-2xl">
                 <div className="flex items-center gap-3 text-cyan-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-6">
                    <span className="w-12 h-px bg-cyan-400"></span>
                    Curated Collections
                 </div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white leading-[1.1]">
                    The <span className="text-cyan-400 font-zentry italic uppercase tracking-tighter">Conservation</span> Paradigm
                 </h2>
              </div>
              <GlassButton 
                onClick={() => window.open('https://zeroartnft.xyz', '_blank')}
                variant="gray"
                size="sm"
                className="px-4 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em]">View Full Marketplace</span>
                  <TiChevronRight className="text-lg" />
                </div>
              </GlassButton>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:h-[800px]">
              {collections.map((coll, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -10 }}
                  className={`relative overflow-hidden rounded-[3rem] border border-white/10 group cursor-pointer 
                    ${index === 0 ? 'md:col-span-4 md:row-span-1' : ''} 
                    ${index === 1 ? 'md:col-span-2 md:row-span-2' : ''}
                    ${index === 2 ? 'md:col-span-2 md:row-span-1' : ''}
                    ${index === 3 ? 'md:col-span-2 md:row-span-1' : ''}
                  `}
                >
                  <img src={coll.image} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" alt={coll.title} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-10 w-full">
                     <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-[8px] font-bold tracking-widest text-white/60 border border-white/10 mb-4 inline-block">{coll.tag}</span>
                     <h4 className="text-2xl font-bold text-white mb-2 uppercase tracking-tight">{coll.title}</h4>
                     <p className="text-gray-300 text-sm max-w-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">{coll.desc}</p>
                  </div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Technical Provenance Hub */}
        <div className="grid lg:grid-cols-2 gap-20 mb-48 items-center" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="relative">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px]" />
            <div className="flex items-center gap-3 text-emerald-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-8">
              <span className="w-12 h-px bg-emerald-400"></span>
              Technical Infrastructure
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-[1.15]">
              Immutable <span className="text-emerald-400 italic font-zentry uppercase tracking-tight">Provenance</span> & Verified <span className="text-emerald-400 italic font-zentry uppercase tracking-tight">Impact</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              Zero-Art utilizes the Zero-Blockchain's consensus to verify the provenance of every asset. 
              Metadata is permanently etched into the Arweave permaweb, ensuring your collection 
              remains accessible for eternity.
            </p>
            <div className="flex flex-wrap gap-4">
               <span className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300 flex items-center gap-2"><FaLock className="text-cyan-500" /> AES-256 Storage</span>
               <span className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300 flex items-center gap-2"><FaFingerprint className="text-cyan-500" /> Biometric Minting</span>
               <span className="px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold tracking-widest uppercase text-gray-300 flex items-center gap-2"><FaShieldAlt className="text-cyan-500" /> IPFS Redundancy</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
             {techFeatures.map((tech, index) => (
                <motion.div
                  key={index}
                  whileHover={{ x: 10, backgroundColor: "rgba(255, 255, 255, 0.08)" }}
                  className="p-12 bg-white/5 border border-white/10 rounded-[3rem] transition-all cursor-default group"
                >
                  <div className="flex flex-col md:flex-row gap-10">
                    <div className="w-20 h-20 rounded-3xl bg-emerald-500/10 flex items-center justify-center text-4xl text-emerald-400 border border-white/5 group-hover:scale-110 transition-transform flex-shrink-0">
                      {tech.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-2xl tracking-tight mb-4 uppercase">{tech.title}</h4>
                      <p className="text-gray-300 text-sm leading-relaxed mb-8">{tech.description}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                         {tech.list.map((item, i) => (
                            <li key={i} className="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                               <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {item}
                            </li>
                         ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
             ))}
          </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-900/40 to-emerald-500/20" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-4xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-5xl text-cyan-400 mx-auto mb-12 shadow-[0_0_50px_rgba(34,211,238,0.2)]">
              <FaLeaf />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Preserve the Future <span className="font-zentry italic text-cyan-400 text-6xl uppercase tracking-wider block mt-4">Through Art</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed max-w-2xl mx-auto">
              The time to act is now. Mint your vision of a sustainable future and 
              become a permanent guardian of the planet's most vital ecosystems.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                onClick={() => window.open('https://zeroartnft.xyz', '_blank')}
                variant="cyan"
                className="px-12 py-6"
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  Explore Marketplace <TiArrowRight className="text-2xl" />
                </div>
              </GlassButton>
              
              <GlassButton
                onClick={() => window.open('https://zeroartnft.xyz', '_blank')}
                variant="gray"
                className="px-12 py-6"
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  Mint Your Vision <TiFlash className="text-2xl text-cyan-400" />
                </div>
              </GlassButton>
            </div>

            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-20 grayscale hover:grayscale-0 transition-all duration-700">
               <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaTerminal /> ZERO-TOKEN POWERED</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaShieldAlt /> IPFS SECURE</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaGlobeAmericas /> PLANETARY IMPACT</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default NFTMarketplace;
